import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, TrendingUp, Siren, Coins, Clock, ArrowUpRight, ArrowDownRight, Minus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/app/Common";
import { ScoreChip } from "@/components/app/HealthGauge";
import { Helpful } from "@/components/app/Playbook";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

const KINDS = { momentum: ["Highest growth momentum", TrendingUp, "text-emerald-500"], attention: ["Needs immediate attention", Siren, "text-red-500"],
  capital: ["Best use of additional capital", Coins, "text-amber-500"], time: ["Best use of owner time", Clock, "text-blue-500"] };
const VERDICT = { invest: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", grow: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  maintain: "bg-slate-500/15 text-slate-600 dark:text-slate-300", fix: "bg-red-500/15 text-red-600 dark:text-red-400", review: "bg-amber-500/15 text-amber-600 dark:text-amber-400" };
const RISK = { low: "text-emerald-500", medium: "text-amber-500", high: "text-red-500" };
const money = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const Trend = ({ a, b, invert }) => {
  if (a == null || b == null) return null;
  const I = a > b ? ArrowUpRight : a < b ? ArrowDownRight : Minus;
  const good = invert ? a < b : a > b;
  return <I className={`inline h-3.5 w-3.5 ${a === b ? "text-muted-foreground" : good ? "text-emerald-500" : "text-red-500"}`} />;
};

const FinDialog = ({ m, onClose, reload }) => {
  const [f, setF] = useState({ revenue_monthly: m.revenue_monthly ?? "", profit_monthly: m.profit_monthly ?? "", cash_on_hand: m.cash_on_hand ?? "" });
  const [busy, setBusy] = useState(false);
  const num = (v) => (v === "" ? null : Number(v));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/portfolio/financials/${m.entity_id}`, { revenue_monthly: num(f.revenue_monthly), profit_monthly: num(f.profit_monthly), cash_on_hand: num(f.cash_on_hand) });
      toast.success("Financials saved");
      reload();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="financials-dialog">
        <DialogHeader><DialogTitle>{m.name} — financials</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Rough monthly numbers are enough. They sharpen Claude's capital and cash-flow advice.</p>
        {[["revenue_monthly", "Monthly revenue"], ["profit_monthly", "Monthly profit"], ["cash_on_hand", "Cash on hand"]].map(([k, l]) => (
          <Field key={k} label={l}><Input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`fin-${k}-input`} /></Field>))}
        <Button onClick={save} disabled={busy} data-testid="financials-save-btn">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save</Button>
      </DialogContent>
    </Dialog>
  );
};

const Headlines = ({ heads }) => (
  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 mt-4" data-testid="portfolio-headlines">
    {Object.entries(KINDS).map(([k, [label, Icon, color]]) => {
      const h = heads.find((x) => x.kind === k);
      return (
        <div key={k} className="rounded-xl border border-border p-4" data-testid={`portfolio-headline-${k}`}>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"><Icon className={`h-3.5 w-3.5 ${color}`} />{label}</div>
          {h ? <><div className="font-semibold mt-2">{h.name}</div><p className="text-sm mt-1">{h.headline}</p><p className="text-xs text-muted-foreground mt-1">{h.why}</p></>
            : <p className="text-sm text-muted-foreground mt-2">Not enough data yet.</p>}
        </div>);
    })}
  </div>
);

const Row = ({ m, ai, canEdit, onFin, stale }) => (
  <tr data-testid={`portfolio-row-${m.entity_id}`}>
    <td className="px-4 py-3"><div className="font-medium">{ai?.rank && ai.rank < 99 ? `${ai.rank}. ` : ""}{m.name}</div>{ai && <span className={`text-[11px] rounded-full px-2 py-0.5 capitalize ${VERDICT[ai.verdict]}`} data-testid={`portfolio-verdict-${m.entity_id}`}>{ai.verdict}</span>}</td>
    <td className="px-4 py-3 whitespace-nowrap"><ScoreChip score={m.health} band={m.band} />{m.health_delta != null && <span className={`ml-1.5 text-xs font-mono ${m.health_delta >= 0 ? "text-emerald-500" : "text-red-500"}`}>{m.health_delta >= 0 ? "+" : ""}{m.health_delta}</span>}</td>
    <td className="px-4 py-3 text-xs whitespace-nowrap">{m.done_4w} done <Trend a={m.done_4w} b={m.done_prior_4w} /><div className="text-muted-foreground">{m.open_tasks} open · {m.overdue_tasks} overdue</div>{ai && <div className="capitalize">{ai.momentum}</div>}</td>
    <td className="px-4 py-3 text-xs">{ai && <div className={`font-semibold capitalize ${RISK[ai.risk]}`}>{ai.risk}</div>}<div className="text-muted-foreground">{m.overdue_obligations} overdue filing/tax{m.tax_due_60d > 0 && ` · ${money(m.tax_due_60d)} tax due 60d`}</div></td>
    <td className="px-4 py-3 text-xs">
      <div title="vs prior 30 days">Spend 30d {money(m.spend_30d)} <Trend a={m.spend_30d} b={m.spend_prior_30d} invert /></div>
      {m.revenue_monthly != null ? <div className="text-muted-foreground">Rev {money(m.revenue_monthly)}/mo <Trend a={m.revenue_monthly} b={m.revenue_prev} /> · cash {money(m.cash_on_hand)}</div> : <div className="text-muted-foreground">No revenue data</div>}
      {ai?.cash && <div className="mt-0.5">{ai.cash}</div>}
      {ai && stale && <div className="mt-0.5 text-amber-600 dark:text-amber-400" data-testid={`portfolio-stale-${m.entity_id}`}>Financials updated — refresh insights</div>}
      {canEdit && <button onClick={() => onFin(m)} className="mt-1 inline-flex items-center gap-1 text-primary hover:underline" data-testid={`portfolio-fin-btn-${m.entity_id}`}><Pencil className="h-3 w-3" />{m.revenue_monthly != null ? "Update" : "Add"} financials</button>}
    </td>
    <td className="px-4 py-3 text-xs max-w-[280px]">{ai ? <><div>{ai.note}</div><div className="mt-1"><b>Next:</b> {ai.next_move}</div></> : <span className="text-muted-foreground">Run insights</span>}</td>
  </tr>
);

export const PortfolioInsights = () => {
  const { user } = useAuth();
  const owner = user?.workspace?.role === "owner";
  const canEdit = user?.workspace?.role !== "viewer";
  const state = useLoad(() => api.get("/portfolio/insights").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const [fin, setFin] = useState(null);
  if (state.gated || state.error) return null;
  const d = state.data;
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/portfolio/insights");
      state.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || errMsg(e));
    }
    setBusy(false);
  };
  const ai = Object.fromEntries((d?.insight?.entities || []).map((x) => [x.entity_id, x]));
  const rows = [...(d?.metrics || [])].sort((a, b) => (ai[a.entity_id]?.rank ?? 99) - (ai[b.entity_id]?.rank ?? 99));
  return (
    <div className="surface p-6 mb-8" data-testid="portfolio-insights">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl"><div className="flex items-center gap-2 font-semibold"><Sparkles className="h-4 w-4 text-primary" />Executive insights
          {d?.insight && <span className="text-xs font-normal text-muted-foreground">· {fmtDate(d.insight.created_at)}</span>}</div>
          <p className="text-sm text-muted-foreground mt-1" data-testid="portfolio-insights-summary">{d?.insight?.summary || "Claude compares every company on health, momentum, risk, cash flow, execution and growth, then says where your time, capital and attention pay off most."}</p></div>
        {owner && <Button onClick={run} disabled={busy || (d?.metrics?.length || 0) < 2} data-testid="portfolio-insights-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{d?.insight ? "Refresh insights" : "Compare my companies"}</Button>}
      </div>
      {d?.insight && <Headlines heads={d.insight.headlines} />}
      {d?.insight?.moves?.length > 0 && <ul className="mt-4 space-y-1 text-sm" data-testid="portfolio-moves">{d.insight.moves.map((x) => <li key={x} className="flex gap-2"><ArrowUpRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />{x}</li>)}</ul>}
      {rows.length > 0 && (
        <div className="overflow-x-auto mt-5 -mx-6 px-6">
          <table className="w-full text-sm min-w-[860px]" data-testid="portfolio-compare-table">
            <thead className="text-xs text-muted-foreground border-b border-border"><tr>{["Company", "Health", "Execution", "Risk", "Cash flow", "Claude's take"].map((h) => <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">{rows.map((m) => <Row key={m.entity_id} m={m} ai={ai[m.entity_id]} canEdit={canEdit} onFin={setFin} stale={!!m.financials_as_of && !!d.insight && m.financials_as_of > d.insight.created_at.slice(0, 10)} />)}</tbody>
          </table>
        </div>)}
      {d?.insight && <div className="mt-3"><Helpful feature="portfolio_insights" itemId={d.insight.created_at} /></div>}
      {fin && <FinDialog m={fin} onClose={() => setFin(null)} reload={state.reload} />}
    </div>
  );
};
